import React, { useState, useEffect } from "react";
import Select from "react-select";
import axios from "axios";
import LoadingOverlay from "../../components/LoadingOverlay";
import AddressSection from "../../components/AddressSection";
import { useNavigate } from "react-router-dom";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import { formatPhoneNumber, phoneDigits } from "../../utils/phone";
import "../../styles/Form.css"; // adjust the path based on location

function UserForm() {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [hearAboutList, setHearAboutList] = useState([]);
  const [recruiterList, setRecruiterList] = useState([]);

  const generateRandomPassword = (length = 12) => {
    const chars =
      "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789@$!%*?&";
    return Array.from(
      { length },
      () => chars[Math.floor(Math.random() * chars.length)],
    ).join("");
  };

  const initialPassword = generateRandomPassword();
  const [confirmPassword, setConfirmPassword] = useState(initialPassword);
  const [profilePic, setProfilePic] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [status, setStatus] = useState([]);
  const [categories, setCategories] = useState([]);
  const [orgUnits, setOrgUnits] = useState([]);
  const [positions, setPositions] = useState([]);
  const [positionTypes, setPositionTypes] = useState([]);
  const [countries, setCountries] = useState([]);
  const [empRoles, setEmpRoles] = useState([]);
  const [empGroups, setEmpGroups] = useState([]);
  const [empOrgs, setEmpOrgs] = useState([]);
  const [canGroups, setCanGroups] = useState([]);
  const [selectedRoles, setSelectedRoles] = useState([]);
  const [selectedGroups, setSelectedGroups] = useState([]);
  const [selectedOrgs, setSelectedOrgs] = useState([]);

  const BASE_URL = process.env.REACT_APP_API_BASE_URL;

  const [form, setForm] = useState({
    userid: "",
    userid_no: "",
    status: "",
    first_name: "",
    mid_name: "",
    last_name: "",
    suffix: "",
    pre_name: "",
    email: "",
    mobile: "",
    home_phone: "",
    hear_about_us: "",
    address1: "",
    address2: "",
    city: "",
    province: "",
    country: "",
    postal_code: "",
    gender: "",
    dob: "",
    refer_by: "",
    password: initialPassword,
    role: "2",
    refer_status: "",
    refer_by_name: "",
    refer_by_id: "",
    emp_category: "",
    cross_border_dot: "",
    candidate_group: "",
    emp_recruiter: "",
    org_unit: "",
    position_full: "",
    emp_roles: "",
    emp_groups: "",
    emp_orgs: "",
  });

  // Associate gate
  const isCandidate = Number(form.status) === 3;
  const isAdmin = Number(form.role) === 1;
  // const isDriver = [2, 8, 11].includes(Number(form.emp_category));

  // Fetch status
  useEffect(() => {
    const fetchStatus = async () => {
      try {
        const res = await axios.get(`${BASE_URL}/api/dropdowns/user-status`, {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        });
        setStatus(res.data);
        const associate = res.data.find(
          (s) => s.s_TranslatedText === "Associate",
        );
        if (associate) {
          setForm((prev) => ({ ...prev, status: associate.s_id }));
        }
      } catch (err) {
        console.error("Failed to fetch status", err);
      }
    };
    fetchStatus();
  }, [BASE_URL]);

  // Fetch categories once
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const res = await axios.get(`${BASE_URL}/api/dropdowns/categories`, {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        });
        setCategories(res.data);
      } catch (err) {
        console.error("Failed to fetch categories", err);
      }
    };
    fetchCategories();
  }, [BASE_URL]);

  // Fetch org units once
  useEffect(() => {
    const fetchOrgUnits = async () => {
      try {
        const res = await axios.get(`${BASE_URL}/api/dropdowns/org-units`, {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        });
        setOrgUnits(res.data);
        setEmpOrgs(res.data);
      } catch (err) {
        console.error("Failed to fetch Org Units", err);
      }
    };
    fetchOrgUnits();
  }, [BASE_URL]);

  useEffect(() => {
    const fetchPositionType = async () => {
      try {
        const res = await axios.get(
          `${BASE_URL}/api/dropdowns/position-types`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          },
        );
        setPositionTypes(res.data);
      } catch (err) {
        console.error("Failed to fetch Position Types", err);
      }
    };
    fetchPositionType();
  }, [BASE_URL]);

  // Fetch positions when category changes
  useEffect(() => {
    const fetchPositions = async () => {
      try {
        const url = form.emp_category
          ? `${BASE_URL}/api/positions/by-category/${form.emp_category}`
          : `${BASE_URL}/api/positions`;

        const res = await axios.get(url, {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        });

        setPositions(res.data);
      } catch (err) {
        console.error("Failed to fetch positions:", err);
      }
    };

    fetchPositions();
  }, [BASE_URL, form.emp_category]);

  useEffect(() => {
    const fetchCountires = async () => {
      try {
        const res = await axios.get(`${BASE_URL}/api/dropdowns/countries`, {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        });
        setCountries(res.data);
      } catch (err) {
        console.error("Failed to fetch countries data", err);
      }
    };
    fetchCountires();
  }, [BASE_URL]);

  useEffect(() => {
    const fetchRoles = async () => {
      try {
        const res = await axios.get(`${BASE_URL}/api/dropdowns/emp-role`, {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        });
        setEmpRoles(res.data);
      } catch (err) {
        console.error("Error fetching roles", err);
      }
    };

    const fetchGroups = async () => {
      try {
        const res = await axios.get(
          `${BASE_URL}/api/dropdowns/position-groups`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          },
        );
        setEmpGroups(res.data);
        setCanGroups(res.data); // always set both
      } catch (err) {
        console.error("Error fetching groups", err);
      }
    };

    // Fetch roles only if Admin
    if (isAdmin) {
      fetchRoles();
    }

    // Fetch groups for everyone
    fetchGroups();
  }, [isAdmin, BASE_URL]);

  useEffect(() => {
    axios
      .get(`${BASE_URL}/api/dropdowns/hear-about-us`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      })
      .then((res) => setHearAboutList(res.data))
      .catch((err) => console.error("Failed to fetch Hear About Us list", err));
  }, [BASE_URL]);

  useEffect(() => {
    axios
      .get(`${BASE_URL}/api/dropdowns/recruiters`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      })
      .then((res) => setRecruiterList(res.data))
      .catch((err) => console.error("Failed to fetch Recruiter list", err));
  }, [BASE_URL]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    const nextValue = ["mobile", "home_phone"].includes(name)
      ? phoneDigits(value)
      : value;
    setForm((previous) => ({ ...previous, [name]: nextValue }));
  };

  const handleGenerateId = async () => {
    try {
      setGenerating(true);
      const token = localStorage.getItem("token");
      const res = await axios.get(`${BASE_URL}/api/employee-number/next`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setForm((prev) => ({ ...prev, userid_no: res.data.employeeNumber }));
    } catch (err) {
      console.error("Failed to get next employee number", err);
      alert("Failed to get next Employee ID");
    } finally {
      setGenerating(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.password !== confirmPassword) {
      alert("Passwords do not match");
      return;
    }

    setIsSubmitting(true);

    try {
      const token = localStorage.getItem("token");
      const formData = new FormData();

      form.emp_roles = JSON.stringify(selectedRoles);
      form.emp_groups = JSON.stringify(selectedGroups);
      form.emp_orgs = JSON.stringify(selectedOrgs);

      const selectedPosition = positions.find(
        (p) => String(p.p_id) === String(form.position_full),
      );
      const positionTitle = selectedPosition?.p_position_title || "";
      const derivedOrgUnit = selectedPosition?.p_org_unit || "";

      const payload = {
        ...form,
        position: positionTitle,
        org_unit: derivedOrgUnit,
        refer_by:
          form.refer_status === "Yes"
            ? JSON.stringify({
                name: (form.refer_by_name || "").trim(),
                employeeId: (form.refer_by_id || "").trim(),
              })
            : null,
      };

      delete payload.refer_by_name;
      delete payload.refer_by_id;
      if (payload.role === "1") payload.dob = "";

      for (const [key, value] of Object.entries(payload)) {
        // ❗ Only append fields with defined names and non-null values
        if (value !== undefined && value !== null) {
          formData.append(key, value);
        }
      }

      // ✅ Always send a named field (even for Admin)
      if (isAdmin) {
        const emptyFile = new File([""], "empty.jpg", { type: "image/jpeg" });
        formData.append("profilePic", emptyFile, "empty.jpg");
      } else if (profilePic instanceof File) {
        formData.append(
          "profilePic",
          profilePic,
          profilePic.name || "profile.jpg",
        );
      } else {
        const emptyFile = new File([""], "empty.jpg", { type: "image/jpeg" });
        formData.append("profilePic", emptyFile, "empty.jpg");
      }

      await axios.post(`${BASE_URL}/api/users`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${token}`,
        },
      });

      await axios.post(
        `${BASE_URL}/api/send-welcome-reset`,
        { email: form.email },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      alert("User saved and reset email sent!");
      navigate("/admin");
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        "❌ Failed to save user. Please check and try again.";
      alert(msg);
    } finally {
      setIsSubmitting(false); // ✅ ensure button resets
    }
  };

  const toggleSelection = (id, list, setList) => {
    setList((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id],
    );
  };

  const positionOptions = positions.map((pos) => {
    const orgName =
      orgUnits.find((o) => o.ou_id === parseInt(pos.p_org_unit))?.ou_name ||
      "Unknown Unit";
    const typeName =
      positionTypes.find((pt) => pt.pt_id === parseInt(pos.p_position_type))
        ?.pt_name || "Unknown Type";
    const city = pos.p_contact_info?.city || "";
    const openings = pos.p_posting?.p_num_openings ?? "N/A";

    return {
      value: pos.p_id, // ⬅️ use primary key
      title: pos.p_position_title,
      orgName,
      typeName,
      displayLabel: `${pos.p_position_title} - ${orgName} - ${typeName} - ${city}`,
      subtext: `Pos No: ${pos.p_position_number} - No. Openings: ${openings}`,
    };
  });

  return (
    <div className="container-fluid my-5">
      {isSubmitting && <LoadingOverlay text="Saving changes..." />}
      <div className="form-wrapper">
        <h4 className="mb-4 text-center">Personal Information</h4>
        <form onSubmit={handleSubmit}>
          {/* Contact Info */}
          <fieldset className="border p-3 mb-4">
            <legend className="w-auto px-2">Contact Information</legend>
            <div className="row mb-3">
              <div className="col-md-3 mb-3">
                <label>
                  Role <span className="text-danger">*</span>
                </label>
                <select
                  name="role"
                  className="form-select"
                  value={form.role}
                  onChange={handleChange}
                  required
                >
                  <option value="">Select</option>
                  <option value="2">User</option>
                  <option value="1">Admin</option>
                </select>
              </div>
              <div className="col-md-3">
                <label>
                  Status <span className="text-danger">*</span>
                </label>
                <select
                  name="status"
                  className="form-select"
                  value={form.status}
                  onChange={handleChange}
                  required
                >
                  <option value="">Select</option>
                  {status
                    .filter((s) => {
                      const role = Number(form.role);
                      const statusId = Number(s.s_id);

                      // Always show Associate (1)
                      if (statusId === 1) return true;

                      // If Admin selected, show only Associate
                      if (role === 1) return false;

                      // If role is User or not selected, show Candidate (3)
                      return statusId === 3;
                    })
                    .map((s) => (
                      <option key={s.s_id} value={s.s_id}>
                        {s.s_TranslatedText}
                      </option>
                    ))}
                </select>
              </div>
            </div>
            <div className="row mb-3">
              <div className="col-md-3">
                <label>
                  First Name <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  name="first_name"
                  className="form-control"
                  value={form.first_name}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="col-md-3">
                <label>Middle Name</label>
                <input
                  type="text"
                  name="mid_name"
                  className="form-control"
                  value={form.mid_name}
                  onChange={handleChange}
                />
              </div>
              <div className="col-md-3">
                <label>
                  Last Name <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  name="last_name"
                  className="form-control"
                  value={form.last_name}
                  onChange={handleChange}
                  required
                />
              </div>
              {!isAdmin && (
                <>
                  <div className="col-md-1">
                    <label>Suffix</label>
                    <input
                      type="text"
                      name="suffix"
                      className="form-control"
                      value={form.suffix}
                      onChange={handleChange}
                    />
                  </div>
                  <div className="col-md-2">
                    <label>Gender</label>
                    <select
                      name="gender"
                      className="form-select"
                      value={form.gender}
                      onChange={handleChange}
                    >
                      <option value="">Select</option>
                      <option value="Male">Male</option>
                      <option value="Female">Female</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                </>
              )}
            </div>

            <div className="row mb-3">
              {!isAdmin && (
                <>
                  <div className="col-md-3">
                    <label>Preferred Name</label>
                    <input
                      type="text"
                      name="pre_name"
                      className="form-control"
                      value={form.pre_name}
                      onChange={handleChange}
                    />
                  </div>
                </>
              )}
              <div className="col-md-3">
                <label>
                  Email <span className="text-danger">*</span>
                </label>
                <input
                  type="email"
                  name="email"
                  placeholder="e.g. user@example.com"
                  className="form-control"
                  value={form.email}
                  onChange={handleChange}
                  required
                />
              </div>

              {!isAdmin && (
                <>
                  <div className="col-md-3">
                    <label>
                      Mobile <span className="text-danger">*</span>
                    </label>
                    <input
                      type="tel"
                      name="mobile"
                      placeholder="e.g. (403) 123-4567"
                      pattern="[(][0-9]{3}[)] [0-9]{3}-[0-9]{4}"
                      title="Please enter a 10-digit phone number"
                      className="form-control"
                      value={formatPhoneNumber(form.mobile)}
                      maxLength={14}
                      inputMode="numeric"
                      onChange={handleChange}
                      required
                    />
                  </div>
                  <div className="col-md-3">
                    <label>Home Phone</label>
                    <input
                      type="tel"
                      name="home_phone"
                      placeholder="e.g. (403) 123-4567"
                      pattern="[(][0-9]{3}[)] [0-9]{3}-[0-9]{4}"
                      title="Please enter a 10-digit phone number"
                      className="form-control"
                      value={formatPhoneNumber(form.home_phone)}
                      maxLength={14}
                      inputMode="numeric"
                      onChange={handleChange}
                    />
                  </div>
                </>
              )}
            </div>

            {!isAdmin && (
              <div className="row mb-3">
                <div className="col-md-3">
                  <label>Referred by someone?</label>
                  <select
                    className="form-select"
                    name="refer_status"
                    value={form.refer_status}
                    onChange={handleChange}
                  >
                    <option value="No">No</option>
                    <option value="Yes">Yes</option>
                  </select>
                </div>

                {form.refer_status === "Yes" ? (
                  <>
                    <div className="col-md-3">
                      <label>Referred By</label>
                      <input
                        type="text"
                        name="refer_by_name"
                        className="form-control"
                        placeholder="Referred by who?"
                        value={form.refer_by_name}
                        onChange={handleChange}
                      />
                    </div>
                    <div className="col-md-3">
                      <label>Employee ID</label>
                      <input
                        type="text"
                        name="refer_by_id"
                        className="form-control"
                        value={form.refer_by_id}
                        onChange={handleChange}
                      />
                    </div>
                  </>
                ) : (
                  <div className="col-md-4"></div>
                )}
              </div>
            )}
            {isCandidate && (
              <div className="row mb-3">
                <div className="col-md-3">
                  <label>
                    How did you hear about us?{" "}
                    <span className="text-danger">*</span>
                  </label>
                  <select
                    name="hear_about_us"
                    className="form-select"
                    value={form.hear_about_us || ""}
                    onChange={handleChange}
                    required
                  >
                    <option value="">Select</option>
                    {hearAboutList.map((item) => (
                      <option key={item.hau_id} value={item.hau_id}>
                        {item.hau_name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            )}
          </fieldset>

          {/* Address Info */}
          {!isAdmin && (
            <>
              <fieldset className="border p-3 mb-4">
                <legend className="w-auto px-2">Current Address</legend>
                {/* When status === 3 -> ONLY City, Province, Country, Postal Code */}
                {isCandidate ? (
                  <AddressSection
                    form={form}
                    setForm={setForm}
                    countries={countries}
                  />
                ) : (
                  <>
                    <div className="row mb-3">
                      <div className="col-md-6">
                        <label>
                          Address 1 <span className="text-danger">*</span>
                        </label>
                        <input
                          type="text"
                          name="address1"
                          className="form-control"
                          value={form.address1}
                          onChange={handleChange}
                          required
                        />
                      </div>
                      <div className="col-md-6">
                        <label>Address 2</label>
                        <input
                          type="text"
                          name="address2"
                          className="form-control"
                          value={form.address2}
                          onChange={handleChange}
                        />
                      </div>
                    </div>

                    <AddressSection
                      form={form}
                      setForm={setForm}
                      countries={countries}
                    />
                  </>
                )}
              </fieldset>
            </>
          )}
          {/* Personal Info */}
          {!isCandidate && form.role && form.role !== "1" && (
            <fieldset className="border p-3 mb-4">
              <legend className="w-auto px-2">
                Enter Personal Information
              </legend>
              <div className="row mb-3">
                <div className="col-md-3">
                  <label>
                    Date of Birth <span className="text-danger">*</span>
                  </label>
                  <input
                    type="date"
                    name="dob"
                    className="form-select"
                    value={form.dob}
                    onChange={handleChange}
                    required
                  />
                </div>
              </div>
            </fieldset>
          )}
          {!isAdmin && (
            <>
              {/* Employement */}
              <fieldset className="border p-3 mb-4">
                <legend className="w-auto px-2">Employement</legend>
                <div className="row mb-3">
                  <div className="col-md-3">
                    <label>
                      Employment Category <span className="text-danger">*</span>
                    </label>
                    <select
                      className="form-select"
                      name="emp_category"
                      value={form.emp_category}
                      onChange={handleChange}
                      required
                    >
                      <option value="">Select</option>
                      {categories.map((cat) => (
                        <option key={cat.c_id} value={cat.c_id}>
                          {cat.c_name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="col-md-3">
                    <label>Cross-Border (DOT)</label>
                    <div className="btn-group w-100" role="group">
                      <input
                        type="radio"
                        className="btn-check"
                        name="cross_border_dot"
                        id="cross_dot_yes"
                        value="1"
                        checked={
                          form.cross_border_dot === "1" ||
                          form.cross_border_dot === 1
                        }
                        onChange={handleChange}
                      />
                      <label
                        className="btn btn-outline-primary"
                        htmlFor="cross_dot_yes"
                      >
                        Yes
                      </label>

                      <input
                        type="radio"
                        className="btn-check"
                        name="cross_border_dot"
                        id="cross_dot_no"
                        value="0"
                        checked={
                          form.cross_border_dot === "0" ||
                          form.cross_border_dot === 0
                        }
                        onChange={handleChange}
                      />
                      <label
                        className="btn btn-outline-secondary"
                        htmlFor="cross_dot_no"
                      >
                        No
                      </label>
                    </div>
                  </div>
                </div>
              </fieldset>

              {/* Assign To */}
              <fieldset className="border p-3 mb-4">
                <legend className="w-auto px-2">Assign To</legend>

                <div className="row mb-3">
                  {/* 💡 Org Unit removed from UI; we auto-derive from position */}

                  <div className="col-md-5">
                    <label>
                      Position <span className="text-danger">*</span>
                    </label>
                    <Select
                      name="position_full"
                      value={
                        positionOptions.find(
                          (opt) =>
                            String(opt.value) === String(form.position_full),
                        ) || null
                      }
                      onChange={(selected) =>
                        setForm((prev) => ({
                          ...prev,
                          position_full: selected?.value || "", // p_id
                        }))
                      }
                      options={positionOptions}
                      placeholder="Select position"
                      isSearchable
                      getOptionLabel={(e) => e.displayLabel}
                      formatOptionLabel={(data, { context }) => {
                        if (context === "menu") {
                          return (
                            <div>
                              <div className="react-select-label">
                                {data.displayLabel}
                              </div>
                              <div className="react-select-subtext">
                                {data.subtext}
                              </div>
                            </div>
                          );
                        }
                        return `${data.title} - ${data.orgName} - ${data.typeName}`;
                      }}
                      // 🔧 fixes: render menu in a portal with high z-index
                      menuPortalTarget={
                        typeof window !== "undefined" ? document.body : null
                      }
                      styles={{
                        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                        menu: (base) => ({ ...base, zIndex: 9999 }),
                      }}
                      // helps avoid clipping/position glitches inside overflow/positioned parents
                      menuPosition="fixed"
                      menuShouldScrollIntoView={false}
                      required
                    />
                  </div>
                </div>

                <div className="row mb-3">
                  <div className="col-md-3">
                    <label>Group</label>
                    <select
                      name="candidate_group"
                      className="form-select"
                      value={form.candidate_group || ""}
                      onChange={handleChange}
                    >
                      <option value="">Select</option>
                      {canGroups.map((g) => (
                        <option key={g.pg_id} value={g.pg_id}>
                          {g.pg_name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="col-md-3">
                    <label>Recruiter</label>
                    <select
                      name="emp_recruiter"
                      className="form-select"
                      value={form.emp_recruiter || ""}
                      onChange={handleChange}
                    >
                      <option value="">Select</option>
                      {recruiterList.map((r) => (
                        <option key={r.value} value={r.value}>
                          {r.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </fieldset>
            </>
          )}

          {!isCandidate && (
            <>
              {/* Account Info */}
              <fieldset className="border p-3 mb-4 account-setup-fieldset">
                <legend className="w-auto px-2">Account Setup</legend>
                {!isAdmin && (
                  <div className="row mb-3 align-items-end">
                    <div className="col-md-5">
                      <label>
                        Employee ID <span className="text-danger">*</span>
                      </label>
                      <div className="account-id-control">
                        <input
                          type="text"
                          name="userid_no"
                          className="form-control"
                          value={form.userid_no}
                          onChange={handleChange}
                          required
                        />

                        <button
                          type="button"
                          onClick={handleGenerateId}
                          className="account-id-generate"
                          disabled={generating}
                          title="Get next available Employee ID"
                          data-no-standard-button="true"
                        >
                          <i className={`fas ${generating ? "fa-spinner fa-spin" : "fa-wand-magic-sparkles"}`} aria-hidden="true" />
                          {generating ? "Generating" : "Generate ID"}
                        </button>
                      </div>

                      <small className="account-field-help">
                        <i className="fas fa-circle-info" aria-hidden="true" />
                        Click “Generate ID” to fetch the next available Employee
                        ID (skips duplicates).
                      </small>
                    </div>
                  </div>
                )}

                <div className="row mb-3">
                  <div className="col-md-5">
                    <label>
                      Password <span className="text-danger">*</span>
                    </label>
                    <div className="account-password-field">
                      <input
                        type={passwordVisible ? "text" : "password"}
                        name="password"
                        className="form-control"
                        value={form.password}
                        onChange={handleChange}
                        autoComplete="new-password"
                      />
                      <button
                        type="button"
                        className="account-password-toggle"
                        onClick={() => setPasswordVisible(!passwordVisible)}
                        aria-label={passwordVisible ? "Hide password" : "Show password"}
                        title={passwordVisible ? "Hide password" : "Show password"}
                        data-no-standard-button="true"
                      >
                        {passwordVisible ? <FaEyeSlash /> : <FaEye />}
                      </button>
                    </div>
                  </div>
                </div>

                <div className="row mb-3">
                  <div className="col-md-5">
                    <label>
                      Confirm Password <span className="text-danger">*</span>
                    </label>
                    <input
                      type={passwordVisible ? "text" : "password"}
                      className="form-control"
                      value={confirmPassword}
                      autoComplete="new-password"
                      onChange={(e) => {
                        handleChange(e);
                        setConfirmPassword(e.target.value);
                      }}
                    />
                  </div>
                </div>
                {!isAdmin && (
                  <div className=" mb-3">
                    <label>Profile Picture</label>
                    {previewUrl && (
                      <div className="mb-2">
                        <img
                          src={previewUrl}
                          alt="Preview"
                          width="100"
                          height="100"
                          style={{ objectFit: "cover", borderRadius: "8px" }}
                        />
                      </div>
                    )}
                    <input
                      type="file"
                      accept="image/*"
                      className="form-control"
                      onChange={(e) => {
                        const file = e.target.files[0];
                        if (file && file instanceof Blob) {
                          setProfilePic(file);
                          setPreviewUrl(URL.createObjectURL(file));
                        } else {
                          setProfilePic(null);
                          setPreviewUrl(null);
                          alert("Please select a valid image file.");
                        }
                      }}
                    />
                  </div>
                )}
              </fieldset>
            </>
          )}

          {form.role && isAdmin && (
            <fieldset className="border p-3 mb-4">
              <legend className="w-auto pb-3">
                Role, Group and Org Units Assignment
              </legend>

              <div className="row">
                <div className="col-md-4">
                  <label>
                    <strong>Roles</strong>
                  </label>
                  <div className="form-check">
                    {empRoles.map((role) => {
                      const isSuperAdminRole = role.r_name === "Super Admin";
                      return (
                        <div key={role.r_id}>
                          <input
                            className="form-check-input"
                            type="checkbox"
                            id={`role-${role.r_id}`}
                            checked={selectedRoles.includes(Number(role.r_id))}
                            onChange={() =>
                              toggleSelection(
                                role.r_id,
                                selectedRoles,
                                setSelectedRoles,
                              )
                            }
                            disabled={isSuperAdminRole}
                          />
                          <label
                            className="form-check-label"
                            htmlFor={`role-${role.r_id}`}
                          >
                            {role.r_name}
                          </label>
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div className="col-md-4">
                  <label>
                    <strong>Groups</strong>
                  </label>
                  <div className="form-check">
                    {empGroups.map((group) => (
                      <div key={group.pg_id}>
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id={`group-${group.pg_id}`}
                          checked={selectedGroups.includes(group.pg_id)}
                          onChange={() =>
                            toggleSelection(
                              group.pg_id,
                              selectedGroups,
                              setSelectedGroups,
                            )
                          }
                        />
                        <label
                          className="form-check-label"
                          htmlFor={`group-${group.pg_id}`}
                        >
                          {group.pg_name}
                        </label>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="col-md-4">
                  <label>
                    <strong>Organizational Units</strong>
                  </label>
                  <div className="form-check">
                    {empOrgs.map((org) => (
                      <div key={org.ou_id}>
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id={`org-${org.ou_id}`}
                          checked={selectedOrgs.includes(org.ou_id)}
                          onChange={() =>
                            toggleSelection(
                              org.ou_id,
                              selectedOrgs,
                              setSelectedOrgs,
                            )
                          }
                        />
                        <label
                          className="form-check-label"
                          htmlFor={`org-${org.ou_id}`}
                        >
                          {org.ou_name}
                        </label>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </fieldset>
          )}

          <div className="d-flex justify-content-between">
            <button
              type="button"
              className="btn btn-secondary px-5"
              onClick={() => navigate("/admin")}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary px-5"
              disabled={isSubmitting}
            >
              Submit
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default UserForm;
